import { ReactComponent as FilterIcon } from "../../assets/icons/filters.svg";
import { ReactComponent as DotsIcon } from "../../assets/icons/more.svg";
import { ReactComponent as ArrowDownIcon } from "../../assets/icons/arrow-down.svg";
import { ReactComponent as ArrowLeftIcon } from "../../assets/icons/chevron-left.svg";
import UserIcon from "../../assets/icons/profile-icon.svg";
import {
  SortingState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, { useEffect, useMemo, useRef, useState } from "react";
import UIPopover from "../../components/Popover";
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { api } from "../../helpers/api";
import moment from "moment";
import { ShowToast } from "../../components/toast";
// import BlockedUsers from "./BlockedUsers";
import clsx from "clsx";
import { Button } from "../../components/Button";
import SearchComponent from "../../components/Search";
import { ConfirmDelete, ServiceForm } from "./components/UserDetails";

const columnHelper = createColumnHelper<any>();

const filterData = (data: any[], search: string) => {
  if (!search) return data;
  return data.filter((item) =>
    item.name.toLowerCase().includes(search.toLowerCase())
  );
};

export default function ServicesTypes() {
  const [sortBy, setSortBy] = useState<"name" | undefined>();
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC" | undefined>();
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [isUserDetailsOpen, setIsUserDetailsOpen] = React.useState<any>(false);
  const [selectedUser, setSelectedUser] = React.useState<any | null>(null);
  const [search, setSearch] = React.useState("");
  const [isBlockedUsers, setIsBlockedUsers] = React.useState(false);
  const loadMoreRef = useRef<any>(null);
  const [filters, setFilters] = useState<{
    date: { startDate?: string; endDate?: string };
    types?: string[];
    selectedDateFilter?: string;
  }>();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [createServiceModal, setCreateServiceModal] = useState(false);
  const [editService, setEditService] = useState<any>(null);
  const queryClient = useQueryClient();

  const handleClose = () => {
    setIsUserDetailsOpen(false);
    setSelectedUser(null);
  };

  const { data, isFetching, isLoading, refetch } = useQuery({
    queryKey: ["service-types"],
    queryFn: () => api.getServices(),
  });

  const columns = [
    columnHelper.accessor("name", {
      header: ({ column }) => {
        return (
          <button
            onClick={() => {
              if (sortBy === "name") {
                setSortOrder(sortOrder === "ASC" ? "DESC" : "ASC");
              } else {
                setSortBy("name");
                setSortOrder("ASC");
              }
            }}
            className="p-0 flex items-center gap-[2px]"
          >
            Service name
            {sortBy === "name" ? (
              sortOrder === "ASC" ? (
                <ArrowDownIcon className="w-4 h-4" />
              ) : (
                <ArrowDownIcon className="w-4 h-4 rotate-[180deg]" />
              )
            ) : null}
          </button>
        );
      },
      cell: (info) => (
        <div className="flex flex-row items-center gap-2.5">
          <span>{info.getValue()}</span>
        </div>
      ),
    }),
    columnHelper.accessor((row) => row.salonCount, {
      header: "# of salons",
    }),
    columnHelper.accessor((row) => row.stylistCount, {
      header: "# of stylists",
    }),
    columnHelper.accessor("createdAt", {
      header: "Added on",
      cell: (info) => moment(info.getValue()).format("MMM DD, YYYY") || "-",
    }),
    columnHelper.accessor("id", {
      header: "Action",
      cell: (info) => (
        <div className="flex flex-row gap-4 w-full items-center">
          <button
            onClick={() => {
              setEditService(info.row.original);
            }}
            className="text-[#7CAA74] text-[13px] poppins font-medium"
          >
            Edit
          </button>
          <button
            onClick={() => {
              setIsDeleteModalOpen(true);
              setSelectedUser(info.row.original);
            }}
            className="text-[#D00] text-[13px] poppins font-medium"
          >
            Delete
          </button>
          {/* <UIPopover
            trigger={
              <Button
                variant="outline"
                className="hover:!border-[#9D9D9D] hover:!bg-transparent !border-[#D7D7D7] !rounded-md h-[2.5rem] w-[2.5rem] flex !p-2 items-center justify-center [&_circle]:fill-black"
              >
                <DotsIcon />
              </Button>
            }
          >
            {(close) => (
              <div className="flex flex-col gap-4 bg-white p-4 rounded-lg shadow-[0px_6px_26px_0px_rgba(28,43,40,0.14)] font-inter mr-4 text-sm">
                <button
                  className="text-start"
                  onClick={() => {
                    setSelectedUser(info.row.original);
                    setIsUserDetailsOpen(info.row.original);
                  }}
                >
                  View profile
                </button>
                <button
                  className="text-[#F54343] text-start"
                  onClick={() => {
                    setIsDeleteModalOpen(true);
                    setSelectedUser(info.row.original);
                  }}
                >
                  Delete user
                </button>
              </div>
            )}
          </UIPopover> */}
        </div>
      ),
    }),
  ];

  const tableData = filterData(data?.data?.services || [], search);
  const table = useReactTable<any>({
    data: tableData || [], // Correctly set the data property
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(), //client-side sorting
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  const hasFilters = useMemo(() => {
    return filters?.date.startDate || (filters?.types?.length || 0) > 0;
  }, [filters]);

  return (
    <div className="flex flex-col gap-4">
      {isDeleteModalOpen && selectedUser && (
        <ConfirmDelete
          onClose={() => setIsDeleteModalOpen(false)}
          userId={selectedUser.id}
          refetch={() => {
            refetch();
          }}
          name={selectedUser.name}
        />
      )}

      <div className="flex flex-row justify-between">
        <h1 className="text-black text-2xl font-bold leading-[28.8px]">
          Manage service types
        </h1>
      </div>
      <div className="bg-white p-5 flex flex-col">
        <div className="flex flex-row justify-between items-center mb-5">
          <div className="flex items-center gap-3">
            <SearchComponent
              onChange={(e) => {
                setSearch(e.target.value);
              }}
              placeholder="Search"
              value={search}
            />
          </div>
          <Button variant="primary" onClick={() => setCreateServiceModal(true)}>
            Add another service type
          </Button>
        </div>
        {/* <SelectedFilters
          filters={filters}
          setFilters={setFilters}
          hasFilters={
            filters?.date.startDate || (filters?.types?.length || 0) > 0
          }
          setTempFilters={setTempFilters}
        /> */}

        <div
          className={`overflow-auto ${
            hasFilters ? "h-[calc(100vh-260px)]" : "h-[calc(100vh-190px)]"
          } relative flex flex-col`}
        >
          <table className="w-full relative">
            <colgroup>
              <col style={{ width: "30%" }} />
              <col style={{ width: "17%" }} />
              <col style={{ width: "17%" }} />
              <col style={{ width: "16%" }} />
              <col style={{ width: "20%" }} />
            </colgroup>
            <thead className="w-full">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="sticky top-0">
                  {headerGroup.headers.map((header, index) => (
                    <th
                      key={header.id}
                      className={clsx(
                        "text-[0.875rem] font-medium text-[#202020] text-start p-0"
                      )}
                    >
                      <div
                        className={clsx(
                          "w-full px-5 py-4 bg-[#F2F7F3] flex flex-row items-center gap-1 transition-all",
                          index === headerGroup.headers.length - 1 &&
                            "rounded-tr-[14px] rounded-br-[14px]",
                          index === 0 && "rounded-tl-[14px] rounded-bl-[14px]"
                        )}
                        // onClick={header.column.getToggleSortingHandler()}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                        {/* {{
                          asc: <ArrowIcon />,
                          desc: <ArrowIcon className="rotate-[180deg]" />,
                        }[header.column.getIsSorted() as string] ?? null} */}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {!isLoading &&
                table.getRowModel().rows.map((row, index) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className={clsx(
                          "px-5 py-4 border-b border-b-[#D7D7D7] font-medium text-[0.8125rem]",
                          // last index no border
                          index === table.getRowModel().rows.length - 1 &&
                            "border-b-0"
                        )}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}

              {isFetching && (
                <TableSkeleton
                  rows={5}
                  columns={table.getAllColumns().length}
                />
              )}
            </tbody>
          </table>
          {!isLoading && !isFetching && tableData.length === 0 && (
            <div className="flex-1 flex justify-center items-center">
              <span className="text-[#999999] text-[0.875rem] font-medium">
                No data available
              </span>
            </div>
          )}
          {/* <div>{isFetching && !isFetchingNextPage ? "Fetching..." : null}</div> */}
        </div>
      </div>
      {createServiceModal && (
        <ServiceForm
          onClose={() => setCreateServiceModal(false)}
          refetch={() => refetch()}
        />
      )}
      {editService && (
        <ServiceForm
          onClose={() => setEditService(null)}
          refetch={() => refetch()}
          service={editService}
        />
      )}
    </div>
  );
}

export function TableSkeleton({
  rows,
  columns,
}: {
  rows: number;
  columns: number;
}) {
  return (
    <>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <tr key={`skeleton-row-${rowIndex}`} className="animate-pulse">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <td
              key={`skeleton-cell-${rowIndex}-${colIndex}`}
              className="px-5 py-4 border-b border-b-[#D7D7D7] font-medium text-[0.8125rem]"
            >
              <div className="w-full h-4 bg-gray-300 rounded-md"></div>
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
